import React, { FC, useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import { usePageStore } from "@/hooks/usePageStore";
import { useAppTheme } from "@/utils/theme";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ActionMenu from "@/components/ActionMenu";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Chip,
  Button,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { SkeletonHeaderButtons } from "@/ui/header/skeleton-button";
import GenericAlertModal from "@/components/AlertModal";
import {
  useGetAppVersions,
  useCreateAppVersion,
  useUpdateAppVersion,
  useDeleteAppVersion,
  useSetCurrentAppVersion,
} from "@/hooks/useAppVersions";
import {
  AppVersion,
  AppVersionPlatformEnum,
  CreateAppVersionPayload,
} from "@/domain/appVersion.interface";

const validationSchema = yup.object().shape({
  platform: yup
    .string()
    .oneOf(Object.values(AppVersionPlatformEnum))
    .required("Platform is required"),
  version: yup.string().required("Version is required"),
  apiVersion: yup.string().required("API Version is required"),
  functionalVersion: yup.string().required("Functional Version is required"),
  releaseNotes: yup.string().required("Release Notes are required"),
  storeUrl: yup
    .string()
    .url("Must be a valid URL")
    .when("platform", {
      is: (val: string) =>
        val === AppVersionPlatformEnum.IOS ||
        val === AppVersionPlatformEnum.ANDROID,
      then: (schema) =>
        schema.required("Store URL is required for iOS and Android"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const AppVersionsPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { showSnackBar } = useFeedBack();

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<AppVersion | null>(
    null,
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<AppVersion | null>(
    null,
  );

  const { appVersions, isLoadingAppVersions, refetchAppVersions } =
    useGetAppVersions();
  const createMutation = useCreateAppVersion();
  const updateMutation = useUpdateAppVersion();
  const deleteMutation = useDeleteAppVersion();
  const setCurrentMutation = useSetCurrentAppVersion();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CreateAppVersionPayload>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      platform: AppVersionPlatformEnum.BACKOFFICE,
      version: "",
      apiVersion: "",
      functionalVersion: "",
      releaseNotes: "",
      storeUrl: "",
    },
  });

  const selectedPlatform = watch("platform");
  const showStoreUrl =
    selectedPlatform === AppVersionPlatformEnum.IOS ||
    selectedPlatform === AppVersionPlatformEnum.ANDROID;

  React.useEffect(() => {
    setPageTitle("App Versions Management");
  }, [setPageTitle]);

  const handleOpenDialog = (version?: AppVersion) => {
    if (version) {
      setSelectedVersion(version);
      reset({
        platform: version.platform,
        version: version.version,
        apiVersion: version.apiVersion,
        functionalVersion: version.functionalVersion,
        releaseNotes: version.releaseNotes,
        storeUrl: version.storeUrl,
      });
    } else {
      setSelectedVersion(null);
      reset({
        platform: AppVersionPlatformEnum.BACKOFFICE,
        version: "",
        apiVersion: "",
        functionalVersion: "",
        releaseNotes: "",
        storeUrl: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedVersion(null);
    reset();
  };

  const onSubmit = async (data: CreateAppVersionPayload) => {
    try {
      if (selectedVersion && selectedVersion._id) {
        await updateMutation.mutateAsync({
          appVersionId: selectedVersion._id,
          payload: { ...data, _id: selectedVersion._id },
        });
        showSnackBar("App version updated successfully", "success");
      } else {
        await createMutation.mutateAsync(data);
        showSnackBar("App version created successfully", "success");
      }
      handleCloseDialog();
      refetchAppVersions();
    } catch (error: any) {
      showSnackBar(
        error?.response?.data?.message || "An error occurred",
        "error",
      );
    }
  };

  const handleDelete = async () => {
    if (!versionToDelete || !versionToDelete._id) return;

    try {
      await deleteMutation.mutateAsync(versionToDelete._id);
      showSnackBar("App version deleted successfully", "success");
      setShowDeleteConfirm(false);
      setVersionToDelete(null);
      refetchAppVersions();
    } catch (error: any) {
      showSnackBar(
        error?.response?.data?.message || "An error occurred",
        "error",
      );
    }
  };

  const handleSetCurrent = async (version: AppVersion) => {
    if (!version._id) return;
    try {
      await setCurrentMutation.mutateAsync({
        appVersionId: version._id,
        platform: version.platform,
      });
      showSnackBar("Current version updated successfully", "success");
      refetchAppVersions();
    } catch (error: any) {
      showSnackBar(
        error?.response?.data?.message || "An error occurred",
        "error",
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "platform",
      disablePadding: false,
      label: "Platform",
      isSortable: false,
    },
    {
      id: "functionalVersion",
      disablePadding: false,
      label: "Functional Version",
      isSortable: false,
    },
    {
      id: "version",
      disablePadding: false,
      label: "Version",
      isSortable: false,
    },
    {
      id: "apiVersion",
      disablePadding: false,
      label: "API Version",
      isSortable: false,
    },
    {
      id: "isCurrent",
      disablePadding: false,
      label: "Current",
      isSortable: false,
    },
    {
      id: "actions",
      disablePadding: false,
      label: "Actions",
      isSortable: false,
    },
  ];

  const rows = useMemo(() => {
    if (!appVersions || appVersions.length === 0) return [];

    return appVersions.map((item: AppVersion) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => {
            handleOpenDialog(item);
          },
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => {
            setVersionToDelete(item);
            setShowDeleteConfirm(true);
          },
          icon: <DeleteOutlineIcon fontSize="small" />,
        },
      ];

      return createDataRow(item._id || "", {
        platform: (
          <Chip
            label={item.platform.toUpperCase()}
            size="small"
            color={
              item.platform === "backoffice"
                ? "primary"
                : item.platform === "ios"
                  ? "secondary"
                  : "default"
            }
          />
        ),
        functionalVersion: item.functionalVersion || "-",
        version: item.version || "-",
        apiVersion: item.apiVersion || "-",
        isCurrent: item.isCurrent ? (
          <CheckCircleIcon color="success" />
        ) : (
          <Button
            size="small"
            onClick={() => handleSetCurrent(item)}
            disabled={setCurrentMutation.isPending}
          >
            Set Current
          </Button>
        ),
        actions: <ActionMenu options={actionMenu} />,
      });
    });
  }, [appVersions, setCurrentMutation.isPending]);

  return (
    <Box sx={{ p: 3 }}>
      <Card
        sx={{
          boxShadow: "none",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h4" fontWeight={700}>
              App Versions
            </Typography>
            {isLoadingAppVersions ? (
              <SkeletonHeaderButtons />
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Add New Version
              </Button>
            )}
          </Box>

          <DataTable
            data={rows}
            headCells={headCells}
            order="asc"
            orderBy="platform"
            rowsPerPage={10}
            page={0}
            isLoading={isLoadingAppVersions}
            messageEmptyData="No app versions found"
            showPagination={false}
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {selectedVersion ? "Edit App Version" : "Create New App Version"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="platform"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.platform}>
                      <InputLabel>Platform</InputLabel>
                      <Select {...field} label="Platform">
                        <MenuItem value={AppVersionPlatformEnum.BACKOFFICE}>
                          Backoffice
                        </MenuItem>
                        <MenuItem value={AppVersionPlatformEnum.IOS}>
                          iOS
                        </MenuItem>
                        <MenuItem value={AppVersionPlatformEnum.ANDROID}>
                          Android
                        </MenuItem>
                      </Select>
                      {errors.platform && (
                        <Typography variant="caption" color="error">
                          {errors.platform.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="functionalVersion"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Functional Version"
                      fullWidth
                      error={!!errors.functionalVersion}
                      helperText={errors.functionalVersion?.message}
                      placeholder="v1.0 build 1"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="version"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Version"
                      fullWidth
                      error={!!errors.version}
                      helperText={errors.version?.message}
                      placeholder="1.0.0"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="apiVersion"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="API Version"
                      fullWidth
                      error={!!errors.apiVersion}
                      helperText={errors.apiVersion?.message}
                      placeholder="1.0.0"
                    />
                  )}
                />
              </Grid>

              {showStoreUrl && (
                <Grid item xs={12}>
                  <Controller
                    name="storeUrl"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Store URL"
                        fullWidth
                        error={!!errors.storeUrl}
                        helperText={errors.storeUrl?.message}
                        placeholder="https://..."
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Controller
                  name="releaseNotes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Release Notes"
                      fullWidth
                      multiline
                      rows={4}
                      error={!!errors.releaseNotes}
                      helperText={errors.releaseNotes?.message}
                      placeholder="Enter release notes..."
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {selectedVersion ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <GenericAlertModal
          theme={theme}
          onCancel={() => {
            setShowDeleteConfirm(false);
            setVersionToDelete(null);
          }}
          onClick={handleDelete}
          title="Delete App Version"
          description={`Are you sure you want to delete version ${versionToDelete?.functionalVersion}? This action cannot be undone.`}
          submitText="Delete"
          cancelText="Cancel"
        />
      )}
    </Box>
  );
};

export default AppVersionsPage;
